import React, {forwardRef, useState} from 'react';
import {getCoverPhotoStoragePath} from "../Helpers/utils";
import {Link} from "react-router-dom";
import noReception from "../Images/no_reception.png"
import {BuildingSummary} from "../types/protocols";
import {colorForBuildingType, iconForBuildingType, iconForGroupTag} from "../types/BuildingTypeEnums";
import {placeholderForBuildingType} from "../Helpers/PlaceholderUtils";
import {FirebaseImage, ImageSize} from "../Helpers/ImageLoader";
import {translate} from "../types/EnumTranslations";

interface BuildingListItemProps {
    building: BuildingSummary;
    highlighted: boolean;
    category1Label: string;
    category2Label: string;
    /** Also called for a middle click, so the parent can open a new tab (useOpenPath). */
    onClick: (id: string, event: React.MouseEvent) => void;
    visible: boolean;
    /** Hide the round open button when tapping the card itself opens the building. */
    showOpenButton?: boolean;
}

const BuildingListItem = forwardRef<HTMLLIElement, BuildingListItemProps>(
    ({ building, highlighted, category1Label, category2Label, onClick, visible, showOpenButton = true }, ref) => {
        // The open button is a real link; keep its clicks from also reaching the card.
        const stopCardClick = (event: React.MouseEvent) => event.stopPropagation();

        // Get location text
        const locationLines: string[] = [];
        if (building.locationCategories?.category2?.name) {
            locationLines.push(building.locationCategories.category2.name);
        }
        if (building.locationCategories?.category1?.name) {
            locationLines.push(building.locationCategories.category1.name);
        }

        // Build tags array
        const tags: Array<{ text: string; icon?: string; imgSrc?: string }> = [];

        // Building type tag
        tags.push({
            text: translate(building.buildingType),
            imgSrc: iconForBuildingType(building.buildingType)
        });

        // Group tags
        if (building.groupTags) {
            Object.values(building.groupTags).forEach((tag) => {
                tags.push({
                    text: tag.tag,
                    imgSrc: iconForGroupTag(tag.tag),
                });
            });
        }

        // Category tags (if available)
        if (building.locationCategories?.category3?.name) {
            tags.push({
                text: `Kategori ${building.locationCategories.category3.name}`,
            });
        }

        return (
            <li
                ref={ref}
                style={{
                    display: visible ? 'flex' : 'none',
                    alignItems: 'stretch',
                    cursor: 'pointer',
                    // Fills the list's content width, which the sidebar's
                    // gutters define - see --map-sidebar-content-width.
                    width: '100%',
                    minHeight: '128px',
                    position: 'relative',
                }}
                onClick={(event) => onClick(building.key!, event)}
                onAuxClick={(event) => { if (event.button === 1) onClick(building.key!, event); }}
            >
                {/* Main card */}
                <div
                    className={`dropshadow dropshadow-hover${highlighted ? ' map-list-item--highlighted' : ''}`}
                    style={{
                        width: '100%',
                        backgroundColor: '#fff',
                        borderRadius: '10px',
                        display: 'flex',
                        alignItems: 'flex-start',
                        padding: '14px',
                        gap: '14px',
                    }}
                >
                    {/* Image thumbnail on the left */}
                    <div style={{ position: 'relative', flexShrink: 0 }}>
                        <FirebaseImage
                            storagePath={getCoverPhotoStoragePath(building)}
                            size={ImageSize.THUMBNAIL_SMALL}
                            alt="Building"
                            className="map-list-item-thumb"
                            style={{
                                objectFit: 'cover',
                                objectPosition: 'center',
                                borderRadius: '6px',
                            }}
                            placeholder={placeholderForBuildingType(building.buildingType)}
                        />
                        {building.buildingInfo?.hasPoorReception && (
                            <img
                                src={noReception}
                                alt="No reception"
                                style={{
                                    position: 'absolute',
                                    top: '4px',
                                    right: '4px',
                                    width: '20px',
                                    height: '20px',
                                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                    borderRadius: '4px',
                                    padding: '2px',
                                }}
                            />
                        )}
                    </div>

                    {/* Content section */}
                    <div style={{
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'space-between',
                        minWidth: 0,
                        gap: '8px',
                    }}>
                        {/* Building name */}
                        <strong style={{
                            fontSize: '16px',
                            fontWeight: 700,
                            color: '#000',
                            lineHeight: '1.2',
                            margin: 0,
                        }}>
                            {building.name || 'Namnlös byggnad'}
                        </strong>

                        {/* Location info */}
                        {locationLines.length > 0 && (
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px',
                                fontSize: '12px',
                                color: '#000',
                                lineHeight: '1.2',
                                letterSpacing: '0.2px',
                            }}>
                                <img 
                                    src="/Images/UI/location.png" 
                                    alt="Location" 
                                    style={{ 
                                        height: '22px', 
                                        width: 'auto',
                                        marginRight: '5px',
                                        flexShrink: 0 
                                    }} 
                                />
                                <span>
                                    {locationLines.map((line, index) => (
                                        <React.Fragment key={index}>
                                            {line}
                                            {index < locationLines.length - 1 && <br />}
                                        </React.Fragment>
                                    ))}
                                </span>
                            </div>
                        )}

                        {/* Tags */}
                        <div style={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: '6px',
                            alignItems: 'center',
                        }}>
                            {tags.map((tag, index) => (
                                <span
                                    key={index}
                                    style={{
                                        backgroundColor: '#EFEFEF',
                                        borderRadius: '6px',
                                        padding: '4px 10px',
                                        fontSize: '12px',
                                        color: '#000',
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        fontFamily: 'Source Sans Pro',
                                    }}
                                >
                                    {tag.imgSrc && <img src={tag.imgSrc} style={{ width: '12px', height: '12px', marginRight: '6px' }} alt="" />}
                                    {tag.icon && <i className={`bi ${tag.icon}`} style={{ fontSize: '13px', marginRight: '6px' }} />}
                                    {tag.text}
                                </span>
                            ))}
                        </div>
                    </div>

                    {/* Action button on the right */}
                    {showOpenButton && (
                    <Link
                        to={`/building/${building.key}`}
                        onClick={stopCardClick}
                        onAuxClick={stopCardClick}
                        aria-label="Öppna byggnaden"
                        title="Öppna byggnaden"
                        style={{
                            width: '33px',
                            height: '33px',
                            borderRadius: '50%',
                            backgroundColor: '#b3b3b3',
                            border: 'none',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            flexShrink: 0,
                            alignSelf: 'center',
                            transition: 'background-color 0.2s',
                            padding: 0,
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = '#999999';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = '#b3b3b3';
                        }}
                    >
                        <img 
                            src="/Images/UI/open-building.png" 
                            alt="Open building" 
                            style={{ 
                                width: '16px', 
                                height: '16px',
                                objectFit: 'contain',
                            }} 
                        />
                    </Link>
                    )}
                </div>
            </li>
        );
    }
);

export default BuildingListItem;